"use client";

import React, { useEffect, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, FileCheck2, Loader2, Play, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { formatLeaseDialogCell } from "@/utils/lease-dialog-format";

interface LeaseTestingIndASProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface PaymentDetailRow {
  payment_classification: string;
  payment_type: string;
  payment_amount: number;
  n_payments: number;
  int_incr_text: string;
  incr_per_text: string;
  month_first_payment: number;
}

interface LeaseRow {
  id: string;
  lease_name: string;
  commencement_date: string;
  lease_period: number;
  int_ror: number;
  payment_cycle: string;
  payment_details: PaymentDetailRow[];
}

const FILE_NAME = "Execution_LEASE_AssessmentInp.json";
const OUTPUT_FILE_NAME = "Execution_LEASE_AssessmentOut.json";
const NON_APPLICABLE_CLASSIFICATIONS = new Set(["Guaranteed Residual Value", "Purchase Price", "Penalty for Termination"]);

interface OutputLease {
  lease_info: Record<string, any>;
  payment_details: Array<Record<string, any>>;
  schedule: Array<Record<string, any>>;
}

export default function LeaseTestingIndAS({ setActiveSection }: LeaseTestingIndASProps) {
  const [leases, setLeases] = useState<LeaseRow[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [expandedLeases, setExpandedLeases] = useState<Set<string>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [outputLeases, setOutputLeases] = useState<OutputLease[]>([]);
  const [selectedOutputLease, setSelectedOutputLease] = useState<OutputLease | null>(null);
  const [isOutputDialogOpen, setIsOutputDialogOpen] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(FILE_NAME);
        if (result?.success && Array.isArray(result?.data?.leases)) {
          const loadedLeases = result.data.leases.map((lease: any, index: number) => {
            const existingPaymentRows = (Array.isArray(lease.payment_details) ? lease.payment_details : []).map((detail: any) => ({
              payment_classification: detail.payment_classification || "",
              payment_type: detail.payment_type || "",
              payment_amount: Number(detail.payment_amount) || 0,
              n_payments: Number(detail.n_payments) || 0,
              int_incr_text: Array.isArray(detail.int_incr) ? detail.int_incr.join(",") : "",
              incr_per_text: Array.isArray(detail.incr_per) ? detail.incr_per.join(",") : "",
              month_first_payment: Number(detail.month_first_payment) || 0,
            }));

            const paymentRows = existingPaymentRows.length > 0
              ? existingPaymentRows
              : [{
                  payment_classification: "",
                  payment_type: "",
                  payment_amount: 0,
                  n_payments: 0,
                  int_incr_text: "",
                  incr_per_text: "",
                  month_first_payment: 0,
                }];

            return {
            id: `lease_${Date.now()}_${index}`,
            lease_name: lease.lease_name || "",
            commencement_date: toInputDate(lease.commencement_date || ""),
            lease_period: Number(lease.lease_period) || 0,
            int_ror: Number(lease.int_ror) || 0,
            payment_cycle: lease.payment_cycle || "",
            payment_details: paymentRows,
          };
          });
          setLeases(loadedLeases);
          setExpandedLeases(new Set(loadedLeases.map((lease: LeaseRow) => lease.id)));
        }
      } catch (error) {
        console.warn("[LeaseTestingIndAS] Failed to load data:", error);
      }
    };
    loadData();
    loadOutputData();
  }, []);

  const loadOutputData = async () => {
    try {
      const result = await (window as any).sharePointAPI?.downloadClientJson?.(OUTPUT_FILE_NAME);
      if (result?.success && Array.isArray(result?.data?.leases)) {
        setOutputLeases(result.data.leases);
      } else {
        setOutputLeases([]);
      }
    } catch (error) {
      console.warn("[LeaseTestingIndAS] Failed to load output data:", error);
      setOutputLeases([]);
    }
  };

  const addPaymentDetail = (leaseId: string) => {
    const newRow: PaymentDetailRow = {
      payment_classification: "",
      payment_type: "",
      payment_amount: 0,
      n_payments: 0,
      int_incr_text: "",
      incr_per_text: "",
      month_first_payment: 0,
    };
    setLeases((prev) =>
      prev.map((lease) => (lease.id === leaseId ? { ...lease, payment_details: [...lease.payment_details, newRow] } : lease)),
    );
  };

  const removePaymentDetail = (leaseId: string, rowIndex: number) => {
    setLeases((prev) =>
      prev.map((lease) =>
        lease.id === leaseId ? { ...lease, payment_details: lease.payment_details.filter((_, i) => i !== rowIndex) } : lease,
      ),
    );
  };

  const updateLease = (leaseId: string, field: keyof LeaseRow, value: string | number) => {
    setLeases((prev) => prev.map((lease) => (lease.id === leaseId ? { ...lease, [field]: value } : lease)));
  };

  const updatePaymentDetail = (leaseId: string, rowIndex: number, field: keyof PaymentDetailRow, value: string | number) => {
    setLeases((prev) =>
      prev.map((lease) =>
        lease.id === leaseId
          ? {
              ...lease,
              payment_details: lease.payment_details.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row)),
            }
          : lease,
      ),
    );
  };

  const saveData = async (): Promise<boolean> => {
    if (saveStatus === "saving") return false;
    setSaveStatus("saving");
    try {
      const payload = {
        leases: leases.map((lease) => ({
          lease_name: lease.lease_name || "",
          lease_type: "",
          commencement_date: lease.commencement_date ? fromInputDate(lease.commencement_date) : "",
          lease_period: Number(lease.lease_period) || 0,
          int_ror: Number(lease.int_ror) || 0,
          payment_cycle: lease.payment_cycle || "",
          conditions: {
            c_i: "No",
            c_ii: "No",
            c_iii: "No",
            c_iv: "No",
            c_v: "No",
          },
          payment_details: lease.payment_details.map((row) => {
            const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(row.payment_classification);
            return {
              payment_classification: row.payment_classification || "",
              payment_type: row.payment_type || "",
              payment_amount: Number(row.payment_amount) || 0,
              n_payments: nonApplicable ? 0 : Number(row.n_payments) || 0,
              int_incr: nonApplicable ? [] : parseCommaSeparatedNumbers(row.int_incr_text),
              incr_per: nonApplicable ? [] : parseCommaSeparatedNumbers(row.incr_per_text),
              month_first_payment: nonApplicable ? 0 : Number(row.month_first_payment) || 0,
            };
          }),
        })),
      };

      const fyYear = getFY_YEAR();
      if (!fyYear) throw new Error("FY_YEAR is not configured. Please select an engagement.");

      const base64Content = btoa(JSON.stringify(payload, null, 2));
      const result = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName: FILE_NAME,
        referenceValue: "",
        folderName: "juggernaut",
        fyYear,
        sectionList: ["Execution_LEASE_AssessmentInp"],
      });

      if (!result?.success) throw new Error(result?.error || "Failed to save file");
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return true;
    } catch (error) {
      console.error("[LeaseTestingIndAS] Failed to save data:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
      return false;
    }
  };

  const runLeaseAssessment = async () => {
    if (isRunning || saveStatus === "saving") return;
    setIsRunning(true);
    try {
      const isSaved = await saveData();
      if (!isSaved) {
        throw new Error("Please fix save errors before running assessment.");
      }
      const result = await (window as any).payroll?.run?.("execute_lease_assessment", {
        inputFiles: [],
        options: {
          reference_value: "Lease Assessment",
        },
      });
      if (!result?.ok) {
        throw new Error(result?.error || "Failed to run Lease Assessment");
      }
      await loadOutputData();
    } catch (error) {
      console.error("[LeaseTestingIndAS] Failed to run Lease Assessment:", error);
      alert(`Failed to run Lease Assessment: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsRunning(false);
    }
  };

  const openLeaseOutput = (leaseName: string) => {
    const matched = outputLeases.find(
      (item) => String(item?.lease_info?.["Name of Lease"] || "").trim() === String(leaseName || "").trim(),
    );
    if (!matched) {
      alert("Output not found for this lease. Please click Run first.");
      return;
    }
    setSelectedOutputLease(matched);
    setIsOutputDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Testing</h2>
          <p className="text-gray-400">Maintain payment details for all leases from lease assessment</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runLeaseAssessment} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={isRunning || saveStatus === "saving"}>
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run
              </>
            )}
          </Button>
          <Button onClick={saveData} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saveStatus === "saving"}>
            {saveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          {saveStatus === "saved" && <span className="text-sm text-green-400">Saved!</span>}
          <Button variant="outline" onClick={() => setActiveSection?.("ll-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {leases.length === 0 ? (
        <Card className="border-white/10 bg-black/40">
          <CardContent className="py-8 text-white/70">No leases available. Add leases in Lease Assessment first.</CardContent>
        </Card>
      ) : (
        leases.map((lease) => {
          const isExpanded = expandedLeases.has(lease.id);
          return (
            <Card key={lease.id} className="border-white/10 bg-black/40">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      const next = new Set(expandedLeases);
                      if (isExpanded) next.delete(lease.id);
                      else next.add(lease.id);
                      setExpandedLeases(next);
                    }}
                    className="flex items-center gap-2 text-left text-white hover:bg-white/5 rounded px-2 py-1"
                  >
                    {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    <FileCheck2 className="h-5 w-5 text-green-500" />
                    <span className="font-semibold">{lease.lease_name || "Untitled Lease"}</span>
                  </button>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => openLeaseOutput(lease.lease_name)}
                      size="sm"
                      variant="outline"
                      className="border-white/20 text-white"
                      data-allow-archival-edit={true}
                    >
                      Open
                    </Button>
                    <Button onClick={() => addPaymentDetail(lease.id)} size="sm" className="bg-green-600 hover:bg-green-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Row
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {isExpanded && (
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Name of Lease</Label>
                      <Input value={lease.lease_name} onChange={(e) => updateLease(lease.id, "lease_name", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease Commencement Date</Label>
                      <Input type="date" value={lease.commencement_date} onChange={(e) => updateLease(lease.id, "commencement_date", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease Period (in months)</Label>
                      <Input type="number" value={lease.lease_period} onChange={(e) => updateLease(lease.id, "lease_period", parseInt(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Effective Interest Rate</Label>
                      <Input type="number" value={lease.int_ror} onChange={(e) => updateLease(lease.id, "int_ror", parseFloat(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Payment Cycle</Label>
                      <Select value={lease.payment_cycle} onValueChange={(value) => updateLease(lease.id, "payment_cycle", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment cycle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-white/10">
                      <thead>
                        <tr className="bg-black/60">
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Classification</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Type</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Payment Amount (per month/year)</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">No. of months</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Interval of Increment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Increment %</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Month of First Payment</th>
                          <th className="border border-white/10 px-3 py-2 text-left text-white text-sm">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lease.payment_details.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="border border-white/10 px-3 py-3 text-center text-white/60">
                              No payment rows. Click "Add Row".
                            </td>
                          </tr>
                        ) : (
                          lease.payment_details.map((row, rowIndex) => {
                            const nonApplicable = NON_APPLICABLE_CLASSIFICATIONS.has(row.payment_classification);
                            return (
                              <tr key={`${lease.id}_${rowIndex}`} className="hover:bg-white/5">
                                <td className="border border-white/10 px-2 py-2">
                                  <Select value={row.payment_classification} onValueChange={(value) => updatePaymentDetail(lease.id, rowIndex, "payment_classification", value)}>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select classification" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Fixed Payment">Fixed Payment</SelectItem>
                                      <SelectItem value="Variable Payment">Variable Payment</SelectItem>
                                      <SelectItem value="Guaranteed Residual Value">Guaranteed Residual Value</SelectItem>
                                      <SelectItem value="Purchase Price">Purchase Price</SelectItem>
                                      <SelectItem value="Penalty for Termination">Penalty for Termination</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input value={row.payment_type} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "payment_type", e.target.value)} className="border-white/10 bg-black/40 text-white" />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input type="number" value={row.payment_amount} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "payment_amount", parseFloat(e.target.value) || 0)} className="border-white/10 bg-black/40 text-white" />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input type="number" value={row.n_payments} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "n_payments", parseInt(e.target.value) || 0)} disabled={nonApplicable} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input value={row.int_incr_text} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "int_incr_text", e.target.value)} disabled={nonApplicable} placeholder="e.g. 10,15,18" className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input value={row.incr_per_text} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "incr_per_text", e.target.value)} disabled={nonApplicable} placeholder="e.g. 2,3,5" className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Input type="number" value={row.month_first_payment} onChange={(e) => updatePaymentDetail(lease.id, rowIndex, "month_first_payment", parseInt(e.target.value) || 0)} disabled={nonApplicable} className={`border-white/10 bg-black/40 text-white ${nonApplicable ? "opacity-50 cursor-not-allowed" : ""}`} />
                                </td>
                                <td className="border border-white/10 px-2 py-2">
                                  <Button onClick={() => removePaymentDetail(lease.id, rowIndex)} size="sm" variant="outline" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-white/70 text-sm">
                    Note: The interval of increments and the increment % have to be separated by single commas. For example, if the increment happens in the 10th, 15th and 18th months by 2%, 3% and 5% respectively then enter them as 10,15,18 and 2,3,5 respectively for that payment type.
                  </p>
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      <Dialog open={isOutputDialogOpen} onOpenChange={setIsOutputDialogOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] bg-black border-white/20 text-white overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Lease Output - {selectedOutputLease?.lease_info?.["Name of Lease"] || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-auto space-y-4 pr-1">
            {!selectedOutputLease ? (
              <div className="text-white/60">No lease output selected.</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-white/10">
                    <thead>
                      <tr className="bg-black/60">
                        {Object.keys(selectedOutputLease.lease_info || {}).map((key) => (
                          <th key={key} className="border border-white/10 px-3 py-2 text-left text-white text-sm">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-white/5">
                        {Object.keys(selectedOutputLease.lease_info || {}).map((key) => (
                          <td key={key} className="border border-white/10 px-3 py-2 text-white text-sm">
                            {formatLeaseDialogCell(selectedOutputLease.lease_info?.[key], key)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-white/10">
                    <thead>
                      <tr className="bg-black/60">
                        {(selectedOutputLease.payment_details?.length ? Object.keys(selectedOutputLease.payment_details[0]) : []).map((header) => (
                          <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOutputLease.payment_details || []).map((row, idx) => {
                        const headers = selectedOutputLease.payment_details?.length ? Object.keys(selectedOutputLease.payment_details[0]) : [];
                        return (
                          <tr key={idx} className="hover:bg-white/5">
                            {headers.map((header) => (
                              <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm">
                                {formatLeaseDialogCell(row[header], header)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-white/10">
                    <thead>
                      <tr className="bg-black/60">
                        {(selectedOutputLease.schedule?.length ? Object.keys(selectedOutputLease.schedule[0]) : []).map((header) => (
                          <th key={header} className="border border-white/10 px-3 py-2 text-left text-white text-sm whitespace-nowrap">
                            {header || " "}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOutputLease.schedule || []).map((row, idx) => {
                        const headers = selectedOutputLease.schedule?.length ? Object.keys(selectedOutputLease.schedule[0]) : [];
                        return (
                          <tr key={idx} className="hover:bg-white/5">
                            {headers.map((header) => (
                              <td key={`${idx}-${header}`} className="border border-white/10 px-3 py-2 text-white text-sm whitespace-nowrap">
                                {formatLeaseDialogCell(row[header], header)}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function parseCommaSeparatedNumbers(value: string): number[] {
  if (!value || !value.trim()) return [];
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
    .map((v) => Number(v))
    .filter((v) => !Number.isNaN(v));
}

function toInputDate(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }
  const parts = value.includes("/") ? value.split("/") : [];
  if (parts.length === 3) {
    const month = parts[0].padStart(2, "0");
    const day = parts[1].padStart(2, "0");
    const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
    return `${year}-${month}-${day}`;
  }
  return "";
}

function fromInputDate(inputDate: string): string {
  if (!inputDate) return "";
  const [year, month, day] = inputDate.split("-");
  return `${month}/${day}/${year}`;
}
